import * as THREE from 'three';
import { adoptAnim, exportAnim, type AnimWire } from '../anim/atlas.ts';
import { partMaterial, type BakedFrame, type BakedModel } from './bake.ts';

/**
 * A baked model as it crosses from the bake worker: plain arrays (handed over, not copied) and, instead of
 * materials, where each part's material comes from: the model's own materials in the order a rebuild meets them.
 */
type Arr = Float32Array | Uint8Array | Uint16Array | Uint32Array | Int8Array | Int16Array | Int32Array;

interface PartWire {
  attrs: Record<string, { array: Arr; itemSize: number; normalized: boolean }>;
  index: Arr | null;
  sphere: [number, number, number, number] | null;
  mat: number;
  shared: boolean;
  skin: boolean;
}

interface FrameWire {
  parts: PartWire[];
  glows: { pos: [number, number, number]; color: [number, number, number]; size: number; opacity: number; bone: number }[];
}

export interface ModelWire {
  key: string;
  frames: FrameWire[];
  dynamic: { name: string; base: number[]; frame: FrameWire }[];
  height: number;
  triangles: number;
  anim: AnimWire | null;
}

/** Adds the materials met under `root` to `list` (each once, in traversal order). */
export const collectMaterials = (root: THREE.Object3D, list: THREE.Material[], seen: Set<THREE.Material>): void => {
  root.traverse((o) => {
    if ((o as THREE.Sprite).isSprite) return;
    const m = (o as THREE.Mesh).material;
    if (!m) return;
    for (const x of Array.isArray(m) ? m : [m]) {
      if (seen.has(x)) continue;
      seen.add(x);
      list.push(x);
    }
  });
};

/** Worker side: `b` as plain data, and the buffers to hand over. */
export const toWire = (b: BakedModel, mats: THREE.Material[]): { wire: ModelWire; transfer: ArrayBuffer[] } => {
  const buffers = new Set<ArrayBuffer>();
  const ix = new Map(mats.map((m, i) => [m, i] as const));
  const keep = (a: Arr): Arr => {
    buffers.add(a.buffer as ArrayBuffer);
    return a;
  };
  const frame = (f: BakedFrame): FrameWire => ({
    parts: f.parts.map((p) => {
      const src = p.from;
      const mat = src ? ix.get(src.mat) : undefined;
      if (!src || mat === undefined) throw new Error(`bake wire: ${b.key} has a part from an unlisted material`);
      const g = p.geometry;
      const attrs: PartWire['attrs'] = {};
      for (const [name, a] of Object.entries(g.attributes)) {
        const at = a as THREE.BufferAttribute;
        attrs[name] = { array: keep(at.array as Arr), itemSize: at.itemSize, normalized: at.normalized };
      }
      const s = g.boundingSphere;
      return { attrs, index: g.index ? keep(g.index.array as Arr) : null, sphere: s ? [s.center.x, s.center.y, s.center.z, s.radius] : null, mat, shared: src.shared, skin: src.skin };
    }),
    glows: f.glows.map((gl) => ({ pos: [gl.pos.x, gl.pos.y, gl.pos.z], color: [gl.color.r, gl.color.g, gl.color.b], size: gl.size, opacity: gl.opacity, bone: gl.bone })),
  });
  const anim = b.anim ? exportAnim(b.anim) : null;
  if (anim) buffers.add(anim.samples.buffer as ArrayBuffer);
  const wire: ModelWire = {
    key: b.key,
    frames: b.frames.map(frame),
    dynamic: b.dynamic.map((d) => ({ name: d.name, base: d.base.toArray(), frame: frame(d.frame) })),
    height: b.height,
    triangles: b.triangles,
    anim,
  };
  return { wire, transfer: [...buffers] };
};

/** Main side: the baked model back, its parts on `mats` (the same model rebuilt here). */
export const fromWire = (w: ModelWire, mats: THREE.Material[]): BakedModel => {
  const frame = (f: FrameWire): BakedFrame => ({
    parts: f.parts.map((p) => {
      const src = mats[p.mat];
      if (!src) throw new Error(`bake wire: ${w.key} lost material ${p.mat}`);
      const g = new THREE.BufferGeometry();
      for (const [name, a] of Object.entries(p.attrs)) g.setAttribute(name, new THREE.BufferAttribute(a.array, a.itemSize, a.normalized));
      if (p.index) g.setIndex(new THREE.BufferAttribute(p.index, 1));
      if (p.sphere) g.boundingSphere = new THREE.Sphere(new THREE.Vector3(p.sphere[0], p.sphere[1], p.sphere[2]), p.sphere[3]);
      return { geometry: g, material: partMaterial(src, p.shared, p.skin), from: { mat: src, shared: p.shared, skin: p.skin } };
    }),
    glows: f.glows.map((gl) => ({ pos: new THREE.Vector3(...gl.pos), color: new THREE.Color(...gl.color), size: gl.size, opacity: gl.opacity, bone: gl.bone })),
  });
  return {
    key: w.key,
    frames: w.frames.map(frame),
    dynamic: w.dynamic.map((d) => ({ name: d.name, base: new THREE.Matrix4().fromArray(d.base), frame: frame(d.frame) })),
    height: w.height,
    triangles: w.triangles,
    anim: w.anim ? adoptAnim(w.anim) : null,
  };
};
